// src/contexts/BusyContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { logger } from '@/utils/logger';

interface BusyContextValue {
  showBusy: (message?: string) => void;
  hideBusy: () => void;
  isVisible: boolean;
  message?: string;
}

const BusyContext = createContext<BusyContextValue | undefined>(undefined);

// Singleton bridge para callers fora da árvore React (ex.: o interceptor do
// axios em api.ts, no Chunk 3). Mesmo padrão de `dialogApi` (DialogContext) e
// `toastApi` (ToastContext) — Opção A do plano: reuso da convenção existente.
let busyApi: BusyContextValue | null = null;

export const showBusy = (message?: string): void => {
  if (!busyApi) {
    logger.warn('showBusy chamado antes do BusyProvider montar; ignorado');
    return;
  }
  busyApi.showBusy(message);
};

export const hideBusy = (): void => {
  if (!busyApi) {
    logger.warn('hideBusy chamado antes do BusyProvider montar; ignorado');
    return;
  }
  busyApi.hideBusy();
};

export const BusyProvider = ({ children }: { children: ReactNode }) => {
  // refCount suporta operações lentas concorrentes: o overlay aparece com a
  // primeira showBusy e só some quando a última hideBusy zera o contador.
  const [refCount, setRefCount] = useState(0);
  const [message, setMessage] = useState<string | undefined>(undefined);

  const showBusyImpl = useCallback((msg?: string) => {
    setRefCount((c) => c + 1);
    if (msg !== undefined) setMessage(msg);
  }, []);

  const hideBusyImpl = useCallback(() => {
    setRefCount((c) => {
      const next = Math.max(0, c - 1);
      if (next === 0) setMessage(undefined);
      return next;
    });
  }, []);

  const isVisible = refCount > 0;

  const value = useMemo<BusyContextValue>(
    () => ({ showBusy: showBusyImpl, hideBusy: hideBusyImpl, isVisible, message }),
    [showBusyImpl, hideBusyImpl, isVisible, message]
  );

  // Registra o singleton para callers fora do React. O cleanup só zera se ainda
  // for o mesmo `value`, evitando que um provider remontado seja anulado.
  useEffect(() => {
    busyApi = value;
    return () => {
      if (busyApi === value) {
        busyApi = null;
      }
    };
  }, [value]);

  return <BusyContext.Provider value={value}>{children}</BusyContext.Provider>;
};

export const useBusy = (): BusyContextValue => {
  const ctx = useContext(BusyContext);
  if (!ctx) throw new Error('useBusy deve ser usado dentro de um BusyProvider');
  return ctx;
};

// Dev-only debug export — strip em prod via import.meta.env.DEV.
// Permite smoke manual no console: window.__busyBridge__.showBusy('teste').
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (
    window as Window & {
      __busyBridge__?: { showBusy: typeof showBusy; hideBusy: typeof hideBusy };
    }
  ).__busyBridge__ = { showBusy, hideBusy };
}
